import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, FormControl, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ANIMATIONS, APPLICATION_STATUS, STUDENT_CODE_TYPES, LEAD_BASED_PAINT, CERTIFICATE_STAGE_TYPES } from '@shared/utils/app-static-data';
import { IUser } from 'app/auth/auth.models';
import { IPerson } from 'app/models/People/person';
import { IPersonApplication } from 'app/models/People/person-application';
import { IPersonAddress } from 'app/models/People/personAddress';
import { IPersonContactInformation } from 'app/models/People/personContactInformation';
import { IContactInformation } from 'app/models/common/contactInformation';
import { IAccountAffiliation } from 'app/models/common/accountAffiliation';
import { CommonDataService } from 'app/services/common/common-data.service';
import { DocumentService } from 'app/services/document/document.service';
import { SharedService } from 'app/services/core/shared.service';
import { PersonService } from 'app/services/person/person.service';
import { UserInformationService } from 'app/services/user-info/user-info.service';
import { AddFirmApplicationComponent } from '../../firm/add-firm-application/add-firm-application.component';
import { IPersonApplicationDeficiency } from 'app/models/People/person-application-deficiency';
import { AddIndividualApplicationDeficiencyComponent } from './add-individual-application-deficiency/add-individual-application-deficiency.component';
import { IAddress } from 'app/models/common/address';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationDialogueComponent } from '../../common/confirmation-dialogue/confirmation-dialogue.component';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { PaymentService } from 'app/components/payments/services/payments.service';
import { IPersonCertificate } from 'app/models/People/person-certificate';
import { ConfirmationDialogueWithFieldComponent } from '../../common/confirmation-dialogue-with-field/confirmation-dialogue-with-field.component';
import { AddDocumentComponent } from 'app/components/common/document/add-document.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SelectListDialogueComponent } from '../../common/select-list-dialogue/select-list-dialogue.component';
import { IPersonFilter } from 'app/models/filters/personFilter';
import { AtpApplicationApprovalDialogComponent, AtpApplicationApprovalDialogResult } from 'app/components/application/atp/add-atp-application/atp-application-approval-dialog/atp-application-approval-dialog.component';
import { AddEditIndividualComponent } from 'app/components/individual/add-edit-individual/add-edit-individual.component';
import { MergeIndividualComponent } from 'app/components/individual/merge-individual/merge-individual.component';
import { ILinkApprovalEmailStatus } from 'app/models/common/account-link-approval';

type IndividualApplicationWorkflow = 'chooser' | 'new' | 'link';

interface ILinkPersonMatchOption {
  person: IPerson;
  confidence: number;
  detailLabel: string;
}

@Component({
  selector: 'app-add-individual-application',
  templateUrl: './add-individual-application.component.html',
  styleUrls: ['./add-individual-application.component.scss'],
  animations: ANIMATIONS,
})
export class AddIndividualApplicationComponent implements OnInit {
  public applicationCertificateType: string[] = Object.values(STUDENT_CODE_TYPES);
  public canEdit: boolean = true;
  public loading: boolean = true;
  public allowApplicationChooser: boolean = false;
  public showApplicationChooser: boolean = false;
  public selectedWorkflow: IndividualApplicationWorkflow = 'new';
  public linkValidationSummaryRequested: boolean = false;
  public isApplicationForExistingPerson: boolean = false;
  public updateOnClose: boolean = false;
  public isMobile: boolean = false;
  public date = new Date();
  public uiData = LEAD_BASED_PAINT;
  public applicationStatus = APPLICATION_STATUS;
  public applicationDataForm!: UntypedFormGroup;
  public applicationAttestationForm!: UntypedFormGroup;
  public certificateNumber = null;
  public personApplicationData!: IPersonApplication;
  public userID: string = '';
  public linkUserDisplayControl: FormControl = new FormControl('');
  public filteredLinkUsers: IUser[] = [];
  public linkPersonMatches: ILinkPersonMatchOption[] = [];
  public selectedLinkPersonId: number | null = null;
  public selectedMergeIndividuals: IPerson[] = [];
  public linkPersonLookupLoading: boolean = false;
  public sendingLinkApprovalEmails: boolean = false;
  public loadingLinkApprovalEmailStatus: boolean = false;
  public linkApprovalEmailStatus: ILinkApprovalEmailStatus | null = null;
  public cardColor: string = this.sharedService.gold + '20';
  public issueDate: string = '';
  private unmaskedSSN: string = "";
  public personSearchInput$ = new Subject<string>();
  private readonly linkUserSearchInput$ = new Subject<string>();

  public applicationDeficiencies:IPersonApplicationDeficiency[] = [];
  public filteredApplicationDeficiencies:IPersonApplicationDeficiency[] = [];
  public applicationDeficiencyStatusFilterString: FormControl = new FormControl('both');
  public applicationDeficiencyFilterString: string = '';
  private readonly applicationDeficiencyDebounceString: Subject<string> = new Subject<string>();
  public deficiencyPageSize: number = 5;
  public displayedDeficiencyColumns: string[] = ['id', 'description', 'isResolved'];
  public displayedDeficiencyColumnsFormat = [
    {displayName:'Id',columnName:'id', type:'number', size:'10'},
    {displayName:'Description',columnName:'description', type:'string', size:'70'},
    {displayName:'Resolved',columnName:'isResolved', type:'boolean', size:'10'},
  ];
  public deficiencyDataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  public changeIndicator: number = 0;
  @ViewChild('DeficiencyTablePaginator') deficiencyPaginator!: MatPaginator;
  @ViewChild('DeficiencyTableSort') deficiencySort!: MatSort;

  public tableApplicationDocumentsColumns = ['id', 'fileName', 'fileDescription', 'actions']
  public tableApplicationDocumentsFormat = [        
    {displayName:'Id',columnName:'id', type:'number', size:'10'}, 
    {displayName:'File Name',columnName:'fileName', type:'string', size:'30'},
    {displayName:'File Description',columnName:'fileDescription', type:'string', size:'40'},
  ];
  public tableApplicationDocumentsData: any[] = [];
  public tableApplicationDocumentsFilteredData: any[] = [];
  public tableApplicationDocumentsPageSize = 5;
  public filterDocumentString: string = '';  
  public documentDataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  @ViewChild('DocumentTablePaginator') documentPaginator!: MatPaginator;
  @ViewChild('DocumentTableSort') documentSort!: MatSort;

  constructor(@Inject(MAT_DIALOG_DATA) public data: {application: IPersonApplication | null, edit: boolean, renewal: boolean, certificate: IPersonCertificate | null, person: IPerson | null, personAddress: IPersonAddress | null, personContact: IPersonContactInformation | null},
    private readonly dialogRef: MatDialogRef<AddIndividualApplicationComponent>,
    private readonly dialog: MatDialog,
    public personService: PersonService,
    public userService: UserInformationService,
    private readonly toastr: ToastrService,
    public commonService: CommonDataService,
    public documentService: DocumentService,
    public paymentService: PaymentService,
    public sharedService: SharedService,
    private readonly breakpointObserver: BreakpointObserver,) {      
      this.data = this.data ?? {application: null, edit: true, renewal: false, certificate: null, person: null, personAddress: null, personContact: null};
      this.isApplicationForExistingPerson = this.data?.person !== null && this.data?.person !== undefined;
      this.allowApplicationChooser = !this.data?.application && !this.data?.person && !this.data?.renewal && !this.data?.certificate;
      this.selectedWorkflow = this.allowApplicationChooser ? 'chooser' : 'new';
      this.personApplicationData = this.data?.application ?? this.createContextualDraftApplication();
      this.data.application = this.data.application ?? this.personApplicationData;
      this.canEdit = this.personApplicationData.status !== APPLICATION_STATUS.approved;
      this.initializeDebounceFunctions();

      this.breakpointObserver.observe([
        Breakpoints.Handset,
        Breakpoints.Tablet,
        Breakpoints.Small,
      ]).subscribe(result => {
        this.isMobile = result.matches;
      });

      this.personSearchInput$
      .pipe(
        debounceTime(500),          // Wait 300ms after the user stops typing
        distinctUntilChanged(),     // Only emit if the input has changed
        tap((searchTerm) => {        
          return this.performPersonSearch(searchTerm);
        })
      ).subscribe();

      this.linkUserSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      ).subscribe(searchTerm => {
        this.searchLinkUsers(searchTerm);
      });
  }

  ngOnInit(): void {    
    this.setApplicationDataFormFields();
    this.configureApplicationDataForm();
    this.loadData();
  }

  get isLinkApplication(): boolean {
    return this.selectedWorkflow === 'link';
  }

  get dialogTitle(): string {
    if (this.showApplicationChooser) {
      return 'Individual Application';
    }

    if ((this.personApplicationData?.id ?? 0) === 0) {
      return this.isLinkApplication ? 'New Individual Link Request' : 'New Individual Application';
    }

    const name = [this.personApplicationData?.firstName, this.personApplicationData?.lastName].filter(Boolean).join(' ');
    if (this.isLinkApplication) {
      return `${this.personApplicationData.id}: ${name || 'Individual'} - Link Request`;
    }

    const applicationType = this.personApplicationData?.certificateType || 'Application';
    return `${this.personApplicationData.id}: ${name || 'Individual'} - ${applicationType}`;
  }

  get canReturnToChooser(): boolean {
    return this.allowApplicationChooser
      && !this.showApplicationChooser
      && (this.personApplicationData?.id ?? 0) === 0;
  }

  isApplicationApproved(): boolean {
    return this.personApplicationData?.status === APPLICATION_STATUS.approved;
  }

  loadData(): void {
    this.loading = true;
    this.getUser();

    if (this.data?.application?.id) {
      this.personService.getPersonApplicationById(this.data.application.id).subscribe(result => {
        this.personApplicationData = result;
        this.data.application = result;
        this.selectedWorkflow = this.getWorkflowFromApplication(result);
        this.showApplicationChooser = false;
        this.isApplicationForExistingPerson = !!result?.person || this.isApplicationForExistingPerson;
        this.canEdit = this.personApplicationData.status !== APPLICATION_STATUS.approved && this.data.edit !== false;
        this.configureApplicationDataForm();
        this.loadFormFields();
        if (this.isLinkApplication) {
          this.hydrateLinkUserDisplay(this.personApplicationData.userID);
        } else {
          this.linkUserDisplayControl.setValue('', { emitEvent: false });
          this.filteredLinkUsers = [];
        }
        this.selectedLinkPersonId = this.personApplicationData.person?.id ?? null;
        if (this.isLinkApplication) {
          this.refreshLinkPersonMatches();
        }
        this.refreshLinkApprovalEmailStatus();
        this.updatePersonApplicationDeficiencies();
        this.refreshTableApplicationDocuments();
        this.updateSSNRequirement();
        this.loading = false;
      }, error => {
        this.toastr.error('There was an error getting the person application: ', error);
        this.loading = false;
      });

      return;
    }

    this.personApplicationData = this.allowApplicationChooser
      ? this.createStandaloneDraftApplication(false)
      : this.createContextualDraftApplication();

    this.data.application = this.personApplicationData;
    this.showApplicationChooser = this.allowApplicationChooser;
    this.selectedWorkflow = this.showApplicationChooser ? 'chooser' : 'new';
    this.canEdit = true;
    this.resetLinkWorkflowState();
    this.configureApplicationDataForm();
    this.loadFormFields();
    this.updateSSNRequirement();
    this.refreshLinkApprovalEmailStatus();
    this.loading = false;
  }

  selectNewApplication(): void {
    this.selectedWorkflow = 'new';
    this.showApplicationChooser = false;
    this.linkValidationSummaryRequested = false;
    this.personApplicationData = this.createStandaloneDraftApplication(false);
    this.data.application = this.personApplicationData;
    this.isApplicationForExistingPerson = false;
    this.canEdit = true;
    this.resetLinkWorkflowState();
    this.configureApplicationDataForm();
    this.loadFormFields();
    this.updateSSNRequirement();
    this.refreshLinkApprovalEmailStatus();
  }

  selectLinkApplication(): void {
    this.selectedWorkflow = 'link';
    this.showApplicationChooser = false;
    this.linkValidationSummaryRequested = false;
    this.personApplicationData = this.createStandaloneDraftApplication(true);
    this.data.application = this.personApplicationData;
    this.isApplicationForExistingPerson = false;
    this.canEdit = true;
    this.resetLinkWorkflowState();
    this.configureApplicationDataForm();
    this.loadFormFields();
    this.updateSSNRequirement();
    this.refreshLinkApprovalEmailStatus();
  }

  goToPreviousView(): void {
    if (!this.canReturnToChooser) {
      return;
    }

    this.showApplicationChooser = true;
    this.selectedWorkflow = 'chooser';
    this.linkValidationSummaryRequested = false;
    this.personApplicationData = this.createStandaloneDraftApplication(false);
    this.data.application = this.personApplicationData;
    this.isApplicationForExistingPerson = false;
    this.resetLinkWorkflowState();
    this.configureApplicationDataForm();
    this.loadFormFields();
    this.updateSSNRequirement();
  }

  private resetLinkWorkflowState(): void {
    this.linkValidationSummaryRequested = false;
    this.linkUserDisplayControl.setValue('', { emitEvent: false });
    this.filteredLinkUsers = [];
    this.linkPersonMatches = [];
    this.selectedLinkPersonId = null;
    this.selectedMergeIndividuals = [];
    this.linkPersonLookupLoading = false;
    this.linkApprovalEmailStatus = null;
    this.loadingLinkApprovalEmailStatus = false;
  }

  private getDefaultLinkApprovalEmailStatus(disabledReason: string): ILinkApprovalEmailStatus {
    return {
      requestType: 'Individual',
      applicationId: this.personApplicationData?.id ?? 0,
      hasSelectedEntity: !!this.personApplicationData?.person?.id,
      hasValidContactEmails: false,
      validContactEmails: [],
      lastApprovalEmailSentAt: null,
      disabledReason,
    };
  }

  private refreshLinkApprovalEmailStatus(): void {
    if (!this.isLinkApplication) {
      this.linkApprovalEmailStatus = null;
      this.loadingLinkApprovalEmailStatus = false;
      return;
    }

    if (!this.personApplicationData?.id) {
      this.linkApprovalEmailStatus = this.getDefaultLinkApprovalEmailStatus('Save the link request before sending approval emails.');
      return;
    }

    this.loadingLinkApprovalEmailStatus = true;
    this.personService.getPersonLinkApprovalEmailStatus(this.personApplicationData).subscribe(status => {
      this.loadingLinkApprovalEmailStatus = false;
      this.linkApprovalEmailStatus = status ?? this.getDefaultLinkApprovalEmailStatus('Unable to evaluate approval email status.');
    }, () => {
      this.loadingLinkApprovalEmailStatus = false;
      this.linkApprovalEmailStatus = this.getDefaultLinkApprovalEmailStatus('Unable to evaluate approval email status.');
    });
  }

  canSendLinkApprovalEmails(): boolean {
    if (!this.isLinkApplication || this.isApplicationApproved() || this.sendingLinkApprovalEmails || this.loadingLinkApprovalEmailStatus) {
      return false;
    }

    if (!this.personApplicationData?.id || !this.personApplicationData?.person?.id) {
      return false;
    }

    const applicationUserId = (this.personApplicationData?.userID || '').trim();
    if (!applicationUserId) {
      return false;
    }

    return this.linkApprovalEmailStatus?.hasValidContactEmails === true;
  }

  getSendLinkApprovalDisabledReason(): string {
    if (!this.isLinkApplication) {
      return '';
    }

    if (this.isApplicationApproved()) {
      return 'Approved link requests cannot send new approval emails.';
    }

    if (this.sendingLinkApprovalEmails) {
      return 'Approval emails are currently being sent.';
    }

    if (this.loadingLinkApprovalEmailStatus) {
      return 'Checking link approval email status...';
    }

    if (!this.personApplicationData?.id) {
      return 'Save the link request before sending approval emails.';
    }

    if (!this.personApplicationData?.person?.id) {
      return 'Select a matching Individual before sending approval emails.';
    }

    const applicationUserId = (this.personApplicationData?.userID || '').trim();
    if (!applicationUserId) {
      return 'Assign a User ID email before sending approval emails.';
    }

    return this.linkApprovalEmailStatus?.disabledReason || 'No valid contact email is available for the selected Individual.';
  }

  getLinkApprovalEmailRecipientsSummary(): string {
    if (!this.isLinkApplication) {
      return '';
    }

    if (this.loadingLinkApprovalEmailStatus) {
      return 'Checking contact emails...';
    }

    const contactEmails = this.linkApprovalEmailStatus?.validContactEmails || [];
    if (contactEmails.length === 0) {
      return this.getSendLinkApprovalDisabledReason();
    }

    return `${contactEmails.length} valid contact email${contactEmails.length === 1 ? '' : 's'}: ${contactEmails.join(', ')}`;
  }

  private createStandaloneDraftApplication(isLinkApplication: boolean): IPersonApplication {
    const date = new Date();

    return {
      id: 0,
      approverEmail: '',
      approvalDate: '',
      userID: '',
      person: null,
      status: APPLICATION_STATUS.inReview,
      applicationDate: date.toISOString(),
      applicationViableDate: '',
      isPublic: false,
      renewal: false,
      previousApplication: '',
      applicationType: isLinkApplication ? 'link' : null,
      ssn: '',
      dateOfBirth: '',
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      phone: '',
      alternatePhone: '',
      faxPhone: '',
      email: '',
      confirmEmail: '',
      website: '',
      mailingStreet: '',
      mailingCity: '',
      mailingState: '',
      mailingCounty: '',
      mailingZip: '',
      certificateType: '',
      attestation: false,
      esignature: '',
      ssnExempt: false,
      ssnExemptStatement: '',
      repLabel: '',
      repPhone: '',
      repAlternatePhone: '',
      repFaxPhone: '',
      repEmail: '',
      repConfirmEmail: '',
      repWebsite: '',
    };
  }

  private createContextualDraftApplication(): IPersonApplication {
    const application = this.createStandaloneDraftApplication(false);
    const renewalSourceApplication = this.getRenewalSourceApplication();

    application.renewal = this.data?.renewal ?? false;
    application.previousApplication = this.data?.certificate?.controlNumber ?? '';
    application.applicationViableDate = this.data?.certificate?.expirationDate
      ? new Date(new Date(this.data.certificate.expirationDate).setDate(new Date(this.data.certificate.expirationDate).getDate() + 1)).toISOString()
      : '';
    application.person = this.isApplicationForExistingPerson ? this.data.person : null;
    application.dateOfBirth = this.isApplicationForExistingPerson ? this.data.person?.dateOfBirth ?? '' : '';
    application.ssn = this.isApplicationForExistingPerson ? this.data.person?.ssn ?? '' : '';
    application.title = this.isApplicationForExistingPerson ? this.data.person?.title ?? '' : '';
    application.firstName = this.isApplicationForExistingPerson ? this.data.person?.firstName ?? '' : '';
    application.lastName = this.isApplicationForExistingPerson ? this.data.person?.lastName ?? '' : '';
    application.middleName = this.isApplicationForExistingPerson ? this.data.person?.middleName ?? '' : '';
    application.phone = this.isApplicationForExistingPerson ? this.data?.personContact?.contactInformation?.phone ?? '' : '';
    application.alternatePhone = this.isApplicationForExistingPerson ? this.data?.personContact?.contactInformation?.cell ?? '' : '';
    application.faxPhone = this.isApplicationForExistingPerson ? this.data?.personContact?.contactInformation?.fax ?? '' : '';
    application.email = this.isApplicationForExistingPerson ? this.data?.personContact?.contactInformation?.email ?? '' : '';
    application.confirmEmail = this.isApplicationForExistingPerson ? this.data?.personContact?.contactInformation?.email ?? '' : '';
    application.website = this.isApplicationForExistingPerson ? this.data?.personContact?.contactInformation?.website ?? '' : '';
    application.mailingCity = this.isApplicationForExistingPerson ? this.data?.personAddress?.address?.city ?? '' : '';
    application.mailingCounty = this.isApplicationForExistingPerson ? this.data?.personAddress?.address?.county ?? '' : '';
    application.mailingState = this.isApplicationForExistingPerson ? this.data?.personAddress?.address?.state ?? '' : '';
    application.mailingStreet = this.isApplicationForExistingPerson ? this.data?.personAddress?.address?.street ?? '' : '';
    application.mailingZip = this.isApplicationForExistingPerson ? this.data?.personAddress?.address?.zip ?? '' : '';
    application.certificateType = this.isApplicationForExistingPerson ? this.data?.certificate?.codeType ?? '' : '';

    if (renewalSourceApplication) {
      application.ssnExempt = renewalSourceApplication.ssnExempt === true;
      application.ssnExemptStatement = renewalSourceApplication.ssnExemptStatement ?? '';
    }

    return application;
  }

  private getRenewalSourceApplication(): IPersonApplication | null {
    if (!this.data?.renewal) {
      return null;
    }

    if (this.data?.application) {
      return this.data.application;
    }

    return this.data?.certificate?.application ?? null;
  }

  private getWorkflowFromApplication(application: IPersonApplication): IndividualApplicationWorkflow {
    const applicationType = (application?.applicationType ?? '').toString().trim().toLowerCase();
    if (applicationType === 'link') {
      return 'link';
    }

    const status = this.normalizeStatus(application?.status);
    const canInferLink = !this.hasStandardApplicationData(application) && [
      this.normalizeStatus(APPLICATION_STATUS.submitted),
      this.normalizeStatus(APPLICATION_STATUS.inReview),
      this.normalizeStatus(APPLICATION_STATUS.actionNeeded),
      this.normalizeStatus(APPLICATION_STATUS.approved),
      this.normalizeStatus(APPLICATION_STATUS.cancelled),
    ].includes(status);

    return canInferLink ? 'link' : 'new';
  }

  private hasStandardApplicationData(application: IPersonApplication | null | undefined): boolean {
    return this.hasText(application?.certificateType)
      || this.hasText(application?.ssn)
      || this.hasText(application?.mailingStreet)
      || application?.renewal === true;
  }

  private configureApplicationDataForm(): void {
    if (!this.applicationDataForm) {
      return;
    }

    const phonePatternValidator = Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/);
    const zipPatternValidator = Validators.pattern(/^\d{5}(-\d{4})?$/);

    this.setControlValidators('applicationDate', this.isLinkApplication ? [] : [Validators.required]);
    this.setControlValidators('applicationViableDate', this.isLinkApplication ? [] : [Validators.required]);
    this.setControlValidators('certificateType', this.isLinkApplication ? [] : [Validators.required]);
    this.setControlValidators('phone', this.isLinkApplication ? [phonePatternValidator] : [Validators.required, phonePatternValidator]);
    this.setControlValidators('alternatePhone', [phonePatternValidator]);
    this.setControlValidators('faxPhone', [phonePatternValidator]);
    this.setControlValidators('email', this.isLinkApplication ? [Validators.email] : [Validators.required, Validators.email]);
    this.setControlValidators('confirmEmail', this.isLinkApplication ? [Validators.email] : [Validators.required, Validators.email]);
    this.setControlValidators('mailingStreet', this.isLinkApplication ? [] : [Validators.required]);
    this.setControlValidators('mailingCity', this.isLinkApplication ? [] : [Validators.required]);
    this.setControlValidators('mailingState', this.isLinkApplication ? [] : [Validators.required]);
    this.setControlValidators('mailingZip', this.isLinkApplication ? [zipPatternValidator] : [Validators.required, zipPatternValidator]);

    if (this.isLinkApplication) {
      this.syncLinkEmailToConfirmation();
    }

    this.applicationDataForm.updateValueAndValidity({ emitEvent: false });
  }

  private setControlValidators(controlName: string, validators: ValidatorFn[]): void {
    const control = this.applicationDataForm?.get(controlName);
    if (!control) {
      return;
    }

    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private syncApplicationModeMetadata(): void {
    this.personApplicationData.applicationType = this.isLinkApplication
      ? 'link'
      : (this.personApplicationData.certificateType || this.personApplicationData.applicationType || null);

    if (this.isLinkApplication) {
      this.personApplicationData.certificateType = '';
      this.personApplicationData.renewal = false;
    }
  }

  private hasText(value: any): boolean {
    return (value ?? '').toString().trim().length > 0;
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status ?? '').toString().trim().toLowerCase();
  }

  hasContactInformationErrors(): boolean {
    const controls = this.applicationDataForm?.controls;

    return !!(
      controls?.['email']?.errors?.emailMismatch
      || controls?.['confirmEmail']?.errors?.emailMismatch
      || controls?.['email']?.invalid
      || controls?.['confirmEmail']?.invalid
      || controls?.['phone']?.invalid
      || controls?.['alternatePhone']?.invalid
      || controls?.['faxPhone']?.invalid
      || controls?.['repEmail']?.errors?.emailMismatch
      || controls?.['repConfirmEmail']?.errors?.emailMismatch
      || controls?.['repEmail']?.invalid
      || controls?.['repConfirmEmail']?.invalid
      || controls?.['repPhone']?.invalid
      || controls?.['repAlternatePhone']?.invalid
      || controls?.['repFaxPhone']?.invalid
    );
  }

  onLinkUserInput(event: any): void {
    const searchValue = event.target.value || '';
    this.personApplicationData.userID = searchValue.trim();
    this.linkUserSearchInput$.next(searchValue);
  }

  getLinkUserDisplay(user: Partial<IUser> | null, fallbackEmail: string = ''): string {
    if (!user) {
      return fallbackEmail || '';
    }

    const fullName = (user.fullName || '').trim();
    const email = (user.email || fallbackEmail || '').trim();

    if (fullName && email) {
      return `${fullName} (${email})`;
    }

    return fullName || email;
  }

  selectLinkUser(user: IUser): void {
    this.personApplicationData.userID = user.email || '';
    this.linkUserDisplayControl.setValue(this.getLinkUserDisplay(user), { emitEvent: false });
    this.filteredLinkUsers = [];
  }

  private searchLinkUsers(searchValue: string): void {
    const normalizedSearchValue = searchValue?.trim() || '';
    if (normalizedSearchValue.length < 2) {
      this.filteredLinkUsers = [];
      return;
    }

    this.commonService.searchB2CUsers(normalizedSearchValue).subscribe({
      next: (users) => {
        this.filteredLinkUsers = users || [];
      },
      error: () => {
        this.filteredLinkUsers = [];
      }
    });
  }

  private hydrateLinkUserDisplay(email: string | null | undefined): void {
    const normalizedEmail = (email || '').trim();
    if (!this.isLinkApplication) {
      this.linkUserDisplayControl.setValue('', { emitEvent: false });
      return;
    }

    if (!normalizedEmail) {
      this.linkUserDisplayControl.setValue('', { emitEvent: false });
      return;
    }

    this.linkUserDisplayControl.setValue(normalizedEmail, { emitEvent: false });

    this.commonService.searchB2CUsers(normalizedEmail).subscribe({
      next: (users) => {
        const matchingUser = (users || []).find(user => (user.email || '').toLowerCase() === normalizedEmail.toLowerCase());
        this.linkUserDisplayControl.setValue(this.getLinkUserDisplay(matchingUser || null, normalizedEmail), { emitEvent: false });
      },
      error: () => {
        this.linkUserDisplayControl.setValue(normalizedEmail, { emitEvent: false });
      }
    });
  }

  refreshLinkPersonMatches(): void {
    if (!this.isLinkApplication) {
      this.linkPersonMatches = [];
      return;
    }

    this.syncLinkEmailToConfirmation();

    const firstName = (this.applicationDataForm.controls['firstName'].value || '').trim();
    const lastName = (this.applicationDataForm.controls['lastName'].value || '').trim();
    const lookupDateOfBirth = this.toLookupDateValue(this.applicationDataForm.controls['dateOfBirth'].value);
    const lookupPhone = (this.applicationDataForm.controls['phone'].value || '').trim();
    const lookupEmail = (this.applicationDataForm.controls['email'].value || '').trim();

    if (!firstName || !lastName) {
      this.linkPersonMatches = [];
      this.selectedLinkPersonId = null;
      return;
    }

    const baseFilter: IPersonFilter = {
      filterString: `${firstName} ${lastName}`.trim(),
      filterStatus: null,
      filterAffiliation: null,
      expirationStartDate: null,
      expirationEndDate: null,
      filterDiscipline: null,
    };

    const hasSupplementalLookup = this.hasText(lookupDateOfBirth) || this.hasText(lookupPhone) || this.hasText(lookupEmail);
    const strictFilter: IPersonFilter = {
      ...baseFilter,
      lookupFirstName: firstName,
      lookupLastName: lastName,
      lookupDateOfBirth,
      lookupPhone: lookupPhone || null,
      lookupEmail: lookupEmail || null,
    };

    this.linkPersonLookupLoading = true;
    forkJoin({
      strictMatches: this.personService.getPeopleFiltered(hasSupplementalLookup ? strictFilter : baseFilter).pipe(catchError(() => of([]))),
      rankedMatches: hasSupplementalLookup ? this.personService.getPeopleFiltered(baseFilter).pipe(catchError(() => of([]))) : of([]),
    }).subscribe({
      next: ({ strictMatches, rankedMatches }) => {
        const peopleById = new Map<number, IPerson>();
        [...(strictMatches || []), ...(rankedMatches || [])].forEach(person => {
          if (person?.id && !peopleById.has(person.id)) {
            peopleById.set(person.id, person);
          }
        });

        const people = Array.from(peopleById.values());
        if (!people.length) {
          this.linkPersonMatches = [];
          this.selectedLinkPersonId = null;
          this.selectedMergeIndividuals = [];
          this.linkPersonLookupLoading = false;
          return;
        }

        forkJoin(
          people.map(person => this.personService.getPersonContactInformationByPersonId(person.id).pipe(catchError(() => of(null))))
        ).subscribe(contactResults => {
          this.linkPersonMatches = people
            .map((person, index) => this.buildLinkPersonMatchOption(person, contactResults[index]))
            .sort((left, right) => {
              if (right.confidence !== left.confidence) {
                return right.confidence - left.confidence;
              }

              if (left.person.inactive !== right.person.inactive) {
                return left.person.inactive ? 1 : -1;
              }

              return right.person.id - left.person.id;
            });

          const hasExistingSelection = this.linkPersonMatches.some(match => match.person.id === this.personApplicationData.person?.id);
          if (!hasExistingSelection) {
            this.personApplicationData.person = null;
            this.selectedLinkPersonId = null;
          } else {
            this.selectedLinkPersonId = this.personApplicationData.person?.id ?? null;
          }

          this.selectedMergeIndividuals = this.selectedMergeIndividuals.filter(selectedPerson => this.linkPersonMatches.some(match => match.person.id === selectedPerson.id));
          this.linkPersonLookupLoading = false;
        });
      },
      error: () => {
        this.linkPersonMatches = [];
        this.selectedLinkPersonId = null;
        this.selectedMergeIndividuals = [];
        this.linkPersonLookupLoading = false;
      }
    });
  }

  private buildLinkPersonMatchOption(person: IPerson, contactResult: IPersonContactInformation | null): ILinkPersonMatchOption {
    const submittedDob = this.normalizeDateOnly(this.applicationDataForm.controls['dateOfBirth'].value || this.personApplicationData.dateOfBirth || '');
    const candidateDob = this.normalizeDateOnly(person.dateOfBirth || '');
    const submittedPhone = this.normalizePhone(this.applicationDataForm.controls['phone'].value || this.personApplicationData.phone || '');
    const submittedEmail = this.normalizeEmail(this.applicationDataForm.controls['email'].value || this.personApplicationData.email || '');
    const contactInfo = contactResult?.contactInformation;

    const phoneMatched = !!submittedPhone && [contactInfo?.phone, contactInfo?.cell, contactInfo?.fax].some(value => this.normalizePhone(value) === submittedPhone);
    const emailMatched = !!submittedEmail && this.normalizeEmail(contactInfo?.email) === submittedEmail;
    const dobMatched = !!submittedDob && !!candidateDob && submittedDob === candidateDob;

    let confidence = 45;
    if (dobMatched) {
      confidence += 25;
    }
    if (phoneMatched) {
      confidence += 15;
    }
    if (emailMatched) {
      confidence += 15;
    }
    if (!person.inactive) {
      confidence += 10;
    }

    if (!submittedDob && !submittedPhone && !submittedEmail) {
      confidence = person.inactive ? 45 : 55;
    }

    const detailParts = [
      dobMatched ? 'DOB match' : (submittedDob ? 'DOB differs' : 'DOB not provided'),
      submittedPhone ? (phoneMatched ? 'Phone match' : 'Phone differs') : 'Phone not provided',
      submittedEmail ? (emailMatched ? 'Email match' : 'Email differs') : 'Email not provided',
      person.inactive ? 'Inactive record' : 'Active record',
    ];

    return {
      person,
      confidence: Math.min(confidence, 100),
      detailLabel: detailParts.join(' • '),
    };
  }

  selectLinkPersonById(personId: number | null): void {
    this.selectedLinkPersonId = personId;
    const selectedMatch = this.linkPersonMatches.find(match => match.person.id === personId) || null;
    this.personApplicationData.person = selectedMatch?.person ?? null;
    if (this.data?.application) {
      this.data.application.person = this.personApplicationData.person;
    }
    this.refreshLinkApprovalEmailStatus();
  }

  clearLinkPersonSelection(): void {
    this.selectedLinkPersonId = null;
    this.personApplicationData.person = null;
    if (this.data?.application) {
      this.data.application.person = null;
    }
    this.refreshLinkApprovalEmailStatus();
  }

  openMatchedPerson(person: IPerson | null): void {
    if (!person?.id) {
      return;
    }

    this.personService.getPersonById(person.id).subscribe(result => {
      const dialogRef = this.dialog.open(AddEditIndividualComponent, {
        width: '90%',
        data: { person: result },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.refreshLinkPersonMatches();
      });
    }, error => {
      this.toastr.error('Error loading individual', error);
    });
  }

  toggleLinkMatchSelection(person: IPerson): void {
    const existingIndex = this.selectedMergeIndividuals.findIndex(selectedPerson => selectedPerson.id === person.id);
    if (existingIndex > -1) {
      this.selectedMergeIndividuals.splice(existingIndex, 1);
      return;
    }

    this.selectedMergeIndividuals.push(person);
  }

  isLinkMatchSelected(person: IPerson): boolean {
    return this.selectedMergeIndividuals.some(selectedPerson => selectedPerson.id === person.id);
  }

  mergeLinkIndividuals(): void {
    const dialogRef = this.dialog.open(MergeIndividualComponent, {
      width: '90%',
      autoFocus: false,
      data: this.selectedMergeIndividuals,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectedMergeIndividuals = [];
        this.refreshLinkPersonMatches();
      }
    });
  }

  getMergeTooltip(selectedIndividuals: IPerson[]): string {
    if (selectedIndividuals.length > 0) {
      return 'Merge: ' + selectedIndividuals.map(individual => `${individual.id}: ${individual.firstName} ${individual.lastName}`).join(', ');
    }

    return 'Merges selected individuals';
  }

  getLinkMatchColor(confidence: number): string {
    if (confidence >= 85) {
      return '#2e7d32';
    }

    if (confidence >= 65) {
      return '#ed6c02';
    }

    return '#c62828';
  }

  getLinkMatchBackground(confidence: number): string {
    if (confidence >= 85) {
      return '#e8f5e9';
    }

    if (confidence >= 65) {
      return '#fff3e0';
    }

    return '#ffebee';
  }

  private syncLinkEmailToConfirmation(): void {
    if (!this.isLinkApplication) {
      return;
    }

    const emailValue = this.applicationDataForm.controls['email'].value || '';
    this.applicationDataForm.controls['confirmEmail'].setValue(emailValue, { emitEvent: false });
  }

  private normalizePhone(value: string | null | undefined): string {
    return (value || '').replace(/\D/g, '');
  }

  private normalizeEmail(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private normalizeDateOnly(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }

    const parsedDate = value instanceof Date ? value : new Date(value);
    return isNaN(parsedDate.getTime()) ? '' : new Date(parsedDate.setHours(0, 0, 0, 0)).toISOString();
  }

  private toLookupDateValue(value: any): string | null {
    const normalizedDate = this.normalizeDateOnly(value);
    return normalizedDate || null;
  }

  initializeDebounceFunctions(): void {
    this.applicationDeficiencyDebounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      this.applicationDeficiencyFilterString = value.trim().toLowerCase();
      this.filterDeficiencyTable();     
    });
  }

  personSearch(event: any){         
    const inputElement = event.target as HTMLInputElement;
    this.personSearchInput$.next(inputElement.value);
  }

  performPersonSearch(value: any){         
    if (this.isLinkApplication) {
      return;
    }

    if(value.length > 2 && (this.data?.application?.person === null || this.personApplicationData.person === null)){   
      let firstName = this.applicationDataForm.controls['firstName'].value || '';
      let lastName = this.applicationDataForm.controls['lastName'].value || '';
      
      let stringForFilter = firstName + (firstName && lastName ? ' ' : '') + lastName;
      
      let personFilter: IPersonFilter = {
        filterString: stringForFilter,
        filterStatus: null,
        filterAffiliation: null,
        expirationStartDate: null,
        expirationEndDate: null,
        filterDiscipline: null
      };
      
      this.personService.getPeopleFiltered(personFilter).subscribe(res=>{        
        if(res.length > 0){
          const dialogRef = this.dialog.open(SelectListDialogueComponent, {
            width: '80%',
            data: {
                    items: res, 
                    message: 'Select an Existing Person to Link this Application to, click Cancel if this is a New Person', 
                    title: 'Select/Link an Existing Person',
                    type: 'person'
                  },
            autoFocus: false,});
          dialogRef.afterClosed().subscribe(result => {
            if(result){
              this.applicationDataForm.controls['firstName'].setValue(result.firstName);
              this.applicationDataForm.controls['lastName'].setValue(result.lastName);
              this.applicationDataForm.controls['title'].setValue(result.title);
              this.applicationDataForm.controls['middleName'].setValue(result.middleName);
              this.applicationDataForm.controls['ssn'].setValue(result.ssn);
              this.applicationDataForm.controls['dateOfBirth'].setValue(result.dateOfBirth);
              this.applicationDataForm.controls['isPublic'].setValue(result.response === "Yes");

              this.personService.getPersonAddressesByPersonId(result.id).subscribe(result=>{
                this.applicationDataForm.controls['mailingStreet'].setValue(result[0]?.address.street);
                this.applicationDataForm.controls['mailingCity'].setValue(result[0]?.address.city);
                this.applicationDataForm.controls['mailingState'].setValue(result[0]?.address.state);
                this.applicationDataForm.controls['mailingZip'].setValue(result[0]?.address.zip);
                this.applicationDataForm.controls['mailingCounty'].setValue(result[0]?.address.county);
              },err=>{this.toastr.error("There was an error getting the person addresses: ", err)});

              this.personService.getPersonContactInformationByPersonId(result.id).subscribe(result=>{
                this.applicationDataForm.controls['phone'].setValue(result?.contactInformation.phone);
                this.applicationDataForm.controls['alternatePhone'].setValue(result?.contactInformation.cell);
                this.applicationDataForm.controls['faxPhone'].setValue(result?.contactInformation.fax);
                this.applicationDataForm.controls['email'].setValue(result?.contactInformation.email);
                this.applicationDataForm.controls['confirmEmail'].setValue(result?.contactInformation.email);
                this.applicationDataForm.controls['website'].setValue(result?.contactInformation.website);
              },err=>{this.toastr.error("There was an error getting the person contact information: ", err)});
              this.isApplicationForExistingPerson = true;
              this.personApplicationData.person = result;
            }      
          });
        }
      },err=>{this.toastr.error("There was an error searching for firms: ", err)});
    }
  }

  removePerson(){
    if (this.isLinkApplication) {
      this.personApplicationData.person = null;
      if (this.data?.application) {
        if (this.data?.application) {
          this.data.application.person = null;
        }
      }
      this.isApplicationForExistingPerson = false;
      return;
    }

    this.personApplicationData.person = null
    this.data.person = null;
    if (this.data?.application) {
      this.data.application.person = null;
    }
    this.personApplicationData.renewal = false;
    this.isApplicationForExistingPerson = false;
  }

  onSSNFocus() {
    this.applicationDataForm.get('ssn')!.setValue(this.unmaskedSSN);
  }

  onSSNBlur() {    
    this.unmaskedSSN = this.applicationDataForm.get('ssn')!.value;    
    this.applicationDataForm.get('ssn')!.setValue(this.maskSSN(this.unmaskedSSN));
  }

  maskSSN(ssn: string): string {
    if (!ssn) return '';
    const visibleDigits = ssn.slice(-4);
    const maskedDigits = '*'.repeat(ssn.length - 4);
    return maskedDigits + visibleDigits;
  }

  getUser(){
    this.userService.getUserInfo().subscribe(result=>{
      this.userID = result.email;       
    },error=>{console.error("An error occurred trying to get the userID: ", error)})
  }

  getStatusTypeValues() {    
    let typeValues = (this.personApplicationData.status === APPLICATION_STATUS.approved) ? Object.values(APPLICATION_STATUS) : Object.values(APPLICATION_STATUS).filter(x=>x!==APPLICATION_STATUS.approved);
    return typeValues;
  }
  
  setApplicationDataFormFields(){
    this.applicationDataForm = new UntypedFormGroup({
      status: new UntypedFormControl(""), 
      isPublic: new UntypedFormControl(false), 
      firstName: new UntypedFormControl("", [Validators.required]),
      lastName: new UntypedFormControl("", [Validators.required]),
      middleName: new UntypedFormControl(""),
      title: new UntypedFormControl(""),
      ssn: new UntypedFormControl("", [Validators.required,Validators.pattern(/^(?:\*{7}|^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{4})).*$/)]),
      dateOfBirth: new UntypedFormControl(""),      
      applicationDate: new UntypedFormControl("", [Validators.required]),   
      approvalDate: new UntypedFormControl(""),   
      applicationViableDate: new UntypedFormControl("", [Validators.required]),   
      renewal: new UntypedFormControl({value: false, disabled: true}),      
      previousApplication: new UntypedFormControl({value: "", disabled: this.personApplicationData?.renewal === true}),      
      mailingStreet: new UntypedFormControl("", [Validators.required]),
      mailingCity: new UntypedFormControl("", [Validators.required]),
      mailingState: new UntypedFormControl("", [Validators.required]),
      mailingZip: new UntypedFormControl("", [Validators.required,Validators.pattern(/^\d{5}(-\d{4})?$/)]),
      mailingCounty: new UntypedFormControl(""),
      mailingIsPhysical: new UntypedFormControl(false),
      certificateType: new UntypedFormControl({value: "", disabled: this.personApplicationData?.renewal === true}, Validators.required),
      phone: new UntypedFormControl("", [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/)]),
      alternatePhone: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/)]),
      faxPhone: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/)]),
      email: new UntypedFormControl("", [Validators.required, Validators.email]),
      confirmEmail: new UntypedFormControl("", [Validators.required, Validators.email]), 
      website: new UntypedFormControl(""),
      ssnExempt: new UntypedFormControl(false),
      ssnExemptStatement: new UntypedFormControl(""),
      repLabel: new UntypedFormControl(""),
      repPhone: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/)]),
      repAlternatePhone: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/)]),
      repFaxPhone: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?[-\s]*([0-9]{3})-?([0-9]{4}).*$/)]),
      repEmail: new UntypedFormControl("", [Validators.email]),
      repConfirmEmail: new UntypedFormControl("", [Validators.email]), 
      repWebsite: new UntypedFormControl(""),
    }, {
      validators: [
        this.commonService.emailValidator('email', 'confirmEmail'),  
        this.commonService.emailValidator('repEmail', 'repConfirmEmail'),      
      ]
    }); 

    this.applicationAttestationForm = new UntypedFormGroup({
      attestation: new UntypedFormControl(false, [Validators.requiredTrue]),
      esignature: new UntypedFormControl("", [Validators.required])
    });    
  }

  setPersonApplicationData(){ 
    this.personApplicationData = this.createContextualDraftApplication();
  }

  loadFormFields(){    
    for (const controlName in this.personApplicationData) {
      if (this.applicationDataForm.controls.hasOwnProperty(controlName)) {
        this.applicationDataForm.controls[controlName].setValue(this.personApplicationData[controlName]);
        if(!this.canEdit)
          this.applicationDataForm.controls[controlName].disable();
      } else if (this.applicationAttestationForm.controls.hasOwnProperty(controlName)) {
        this.applicationAttestationForm.controls[controlName].setValue(this.personApplicationData[controlName]);
        if(!this.canEdit)
          this.applicationAttestationForm.controls[controlName].disable();
      }
    }
    this.configureApplicationDataForm();
    this.onSSNBlur();
  }

  updatePersonApplicationData(): void {    
    for (const controlName in this.personApplicationData) {
      if (this.applicationDataForm.controls.hasOwnProperty(controlName)) {
        this.personApplicationData[controlName] = this.applicationDataForm.controls[controlName].value;        
      } else if (this.applicationAttestationForm.controls.hasOwnProperty(controlName)) {
        this.personApplicationData[controlName] = this.applicationAttestationForm.controls[controlName].value;
      }
    } 
    this.personApplicationData.ssn = this.unmaskedSSN;
    this.syncApplicationModeMetadata();
    this.normalizePersonApplicationDateValues();
  }

  private normalizePersonApplicationDateValues(): void {
    this.personApplicationData.applicationDate = this.toApprovalIsoString(this.personApplicationData.applicationDate) || new Date().toISOString();
    this.personApplicationData.applicationViableDate = this.toApprovalIsoString(this.personApplicationData.applicationViableDate);
    this.personApplicationData.approvalDate = this.toApprovalIsoString(this.personApplicationData.approvalDate);
    this.personApplicationData.dateOfBirth = this.toApprovalIsoString(this.personApplicationData.dateOfBirth);
  }

  private buildPersonApplicationSavePayload(): IPersonApplication {
    const normalizedUserId = this.isLinkApplication
      ? (this.personApplicationData.userID || '').trim()
      : ((this.personApplicationData.userID || this.userID || '').trim());

    const applicationDate = this.toApprovalIsoString(this.personApplicationData.applicationDate) || new Date().toISOString();

    return {
      ...this.personApplicationData,
      userID: normalizedUserId,
      applicationDate,
      applicationViableDate: this.toApprovalIsoString(this.personApplicationData.applicationViableDate),
      approvalDate: this.toApprovalIsoString(this.personApplicationData.approvalDate),
      dateOfBirth: this.toApprovalIsoString(this.personApplicationData.dateOfBirth),
    };
  }

  updateSSNRequirement(): void {
    if (this.isLinkApplication) {
      this.applicationDataForm.controls.ssn.clearValidators();
      this.applicationDataForm.controls.ssn.updateValueAndValidity();
      return;
    }

    if(this.applicationDataForm.controls.ssnExempt.value){
      this.applicationDataForm.controls.ssn.clearValidators();      
      this.applicationDataForm.controls.ssn.updateValueAndValidity();
      this.applicationDataForm.controls.ssn.setValue("");
      this.applicationDataForm.controls.ssn.disable();
    } else{
      this.applicationDataForm.controls.ssn.enable();
      this.applicationDataForm.controls.ssn.setValidators([Validators.required,Validators.pattern(/^(?:\*{7}|^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{4})).*$/)]);
      this.applicationDataForm.controls.ssn.updateValueAndValidity();
    }
  }

  savePersonApplication(): void{
    this.syncLinkEmailToConfirmation();
    this.updatePersonApplicationData();
    const applicationToSave = this.buildPersonApplicationSavePayload();
    this.personApplicationData = applicationToSave;
    this.personService.SavePersonApplication(applicationToSave).subscribe(result=>{      
      this.updateOnClose = this.updateOnClose || this.personApplicationData.id === 0;       
      this.toastr.success((this.personApplicationData.id === 0) ? "Application Saved" : "Application Updated");
      this.personApplicationData.id = result.id;     
      this.personApplicationData.applicationType = result.applicationType ?? this.personApplicationData.applicationType;
      this.personApplicationData.person = result.person ?? this.personApplicationData.person;
      this.data.application = result;
      this.selectedLinkPersonId = this.personApplicationData.person?.id ?? this.selectedLinkPersonId;
      this.refreshLinkApprovalEmailStatus();
      this.changeIndicator++;       
    },error=>{
      this.toastr.error("There was an error saving the application: ", error);
      console.error(error);
    })
  }

  refreshTableApplicationDocuments(){
    this.documentService.getDocuments({ ownerType: 'PersonApplication', ownerId: this.personApplicationData.id }).subscribe(result=>{
      this.tableApplicationDocumentsData = result.sort((a, b) => a.fileName.localeCompare(b.fileName));
      this.filterDocumentTable();
    },error=>{
      this.toastr.error("There was an error getting the documents: ", error);
    })
  }

  editDocument(row: any){
    const dialogRef = this.dialog.open(AddDocumentComponent, {
      width: this.isMobile ? '90%' : '30%',
      data: {document: row, entity: 'individual', entityRef: this.personApplicationData},
      autoFocus: false,});
    dialogRef.afterClosed().subscribe(result => {      
        this.refreshTableApplicationDocuments();      
    });
  }

  async downloadFile(row: any) {    
    this.documentService.downloadDocument(row).subscribe({
      error: () => this.toastr.error("An error occurred while downloading the document.")
    });
  }  

  clearFile(row: any) {      
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',      
      data: {message: 'Are you sure you want to <strong>Delete</strong> this Application Document?',title: 'Delete the Application Dcoument'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.documentService.deleteDocument(row.id).subscribe(result=>{
          this.refreshTableApplicationDocuments();
        },
        error=>{
          this.toastr.error("An error occurred while deleting the document: ", error);
        });                 
      }
    })
  }

  filterDocumentTable(): void {    
    this.tableApplicationDocumentsFilteredData = this.tableApplicationDocumentsData;
    
    this.tableApplicationDocumentsFilteredData = this.tableApplicationDocumentsFilteredData.filter(x => {      
      return x.id?.toString().includes(this.filterDocumentString) ||      
        x.fileName?.toString().toLowerCase().includes(this.filterDocumentString.toLowerCase() ||
        x.fileDescription?.toString().toLowerCase().includes(this.filterDocumentString.toLowerCase()))        
    });

    this.updateDocumentDataSource();
  }
  updateDocumentDataSource(){    
    this.documentDataSource = new MatTableDataSource<any>(this.tableApplicationDocumentsFilteredData);
    this.documentDataSource.paginator = this.documentPaginator;
    this.documentDataSource.sort = this.documentSort;    
  }

  approvePerson(){    
    if (this.isLinkApplication) {
      this.approveLinkApplication();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogueWithFieldComponent, {
      width: '400px',
      autoFocus: false,
      data: {
        message:'Are you sure you want to <strong>approve</strong> this application?',
        title:'Approve Application',type:'individual',
        certificateType: this.applicationDataForm.controls.certificateType.value,
        controlNumber: this.personApplicationData.previousApplication, 
        selectedDate: this.applicationDataForm.controls.applicationViableDate.value,
        codeType: this.applicationDataForm.controls.certificateType.value,
      },});
    dialogRef.afterClosed().subscribe(result => {      
      if(result !== false && result !== undefined && result !== null){ 
        this.certificateNumber = result.certificateNumber;
        this.applicationDataForm.controls.previousApplication.setValue(result.controlNumber);
        this.issueDate = result.issueDate;

        this.updatePersonApplicationData();
        this.personApplicationData.status = APPLICATION_STATUS.approved;
        this.personApplicationData.approverEmail = this.userID;
        
        if(this.personApplicationData.approvalDate === null || this.personApplicationData.approvalDate === undefined || this.personApplicationData.approvalDate === "" || isNaN(new Date(this.personApplicationData.approvalDate).getTime())){
          this.personApplicationData.approvalDate = new Date().toISOString();
        }

        if(this.personApplicationData.person !== null && this.personApplicationData.person !== undefined){
          this.approveExistingPersonApplication();
        }
        else{
          this.approveNewPersonApplication();
        }        
      }
    })
  }

  private approveLinkApplication(): void {
    this.syncLinkEmailToConfirmation();
    this.linkValidationSummaryRequested = true;
    this.applicationDataForm.controls['firstName'].markAsTouched();
    this.applicationDataForm.controls['lastName'].markAsTouched();
    this.applicationDataForm.controls['dateOfBirth'].markAsTouched();
    this.applicationDataForm.controls['phone'].markAsTouched();
    this.applicationDataForm.controls['email'].markAsTouched();
    this.applicationAttestationForm.controls['attestation'].markAsTouched();
    this.applicationAttestationForm.controls['esignature'].markAsTouched();

    const validationErrors = this.getLinkValidationErrors();
    if (validationErrors.length > 0) {
      this.toastr.error(
        `Please complete all required fields. ${validationErrors.length} field(s) need attention.`,
        'Form Validation Error',
        { timeOut: 5000 }
      );
      return;
    }

    if (!this.personApplicationData.person?.id) {
      this.toastr.error('Link applications must be associated with an existing individual before approval.');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const applicationDate = this.getApprovalDialogDateValue(this.applicationDataForm.controls['applicationDate'].value || this.personApplicationData.applicationDate) || today;
    const applicationViableDate = this.getApprovalDialogDateValue(this.applicationDataForm.controls['applicationViableDate'].value || this.personApplicationData.applicationViableDate) || applicationDate;
    const approvalDate = this.getApprovalDialogDateValue(this.applicationDataForm.controls['approvalDate'].value || this.personApplicationData.approvalDate) || today;

    const dialogRef = this.dialog.open(AtpApplicationApprovalDialogComponent, {
      width: '500px',
      autoFocus: false,
      data: {
        title: 'Approve Link Request?',
        submissionType: 'link',
        applicationDate,
        applicationViableDate,
        approvalDate,
        atpOrId: ''
      }
    });

    dialogRef.afterClosed().subscribe((result: false | AtpApplicationApprovalDialogResult) => {
      if (!(result && result.confirmed)) {
        return;
      }

      this.applicationDataForm.patchValue({
        applicationDate: result.applicationDate,
        applicationViableDate: result.applicationViableDate,
        approvalDate: result.approvalDate,
      });

      this.updatePersonApplicationData();
      this.personApplicationData.applicationDate = this.toApprovalIsoString(result.applicationDate) || new Date().toISOString();
      this.personApplicationData.applicationViableDate = this.toApprovalIsoString(result.applicationViableDate) || this.personApplicationData.applicationDate;
      this.personApplicationData.approvalDate = this.toApprovalIsoString(result.approvalDate) || new Date().toISOString();
      this.personApplicationData.approverEmail = this.userID;
      this.personApplicationData.status = APPLICATION_STATUS.approved;

      this.personService.approvePersonLinkApplication(this.personApplicationData).subscribe(savedApplication => {
        this.personApplicationData = savedApplication;
        this.data.application = savedApplication;
        this.canEdit = false;
        this.toastr.success('Individual Link Application Approved');
        this.loadFormFields();
        this.dialogRef.close(true);
      }, error => {
        this.toastr.error('There was an error approving the individual link application: ', error);
      });
    });
  }

  sendLinkApprovalEmails(): void {
    if (!this.canSendLinkApprovalEmails()) {
      this.toastr.error(this.getSendLinkApprovalDisabledReason());
      return;
    }

    this.syncLinkEmailToConfirmation();
    this.updatePersonApplicationData();
    this.sendingLinkApprovalEmails = true;

    this.personService.sendPersonLinkApprovalEmails(this.personApplicationData).subscribe(result => {
      this.sendingLinkApprovalEmails = false;

      if (result?.emailsSent > 0) {
        const label = result.emailsSent === 1 ? 'email' : 'emails';
        this.toastr.success(`Approval ${label} sent to ${result.emailsSent} contact recipient(s).`);
        this.refreshLinkApprovalEmailStatus();
        return;
      }

      this.toastr.success('Approval emails queued.');
      this.refreshLinkApprovalEmailStatus();
    }, error => {
      this.sendingLinkApprovalEmails = false;
      this.toastr.error('There was an error sending approval emails for this link request: ', error);
    });
  }

  private getApprovalDialogDateValue(value: any): string {
    if (!value) {
      return '';
    }

    const parsedDate = value instanceof Date ? value : new Date(value);
    return isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().split('T')[0];
  }

  private toApprovalIsoString(value: string | Date | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
  }

  private getLinkValidationErrors(): Array<{ field: string; label: string; step: string }> {
    const errors: Array<{ field: string; label: string; step: string }> = [];
    const firstName = (this.applicationDataForm.controls['firstName'].value || '').trim();
    const lastName = (this.applicationDataForm.controls['lastName'].value || '').trim();
    const userId = (this.personApplicationData.userID || '').trim();
    const esignature = (this.applicationAttestationForm.controls['esignature'].value || '').trim();

    if (!userId) {
      errors.push({ field: 'userID', label: 'User ID', step: 'Admin' });
    }

    if (!firstName) {
      errors.push({ field: 'firstName', label: 'First Name', step: 'Admin' });
    }

    if (!lastName) {
      errors.push({ field: 'lastName', label: 'Last Name', step: 'Admin' });
    }

    if (!this.hasText(this.applicationDataForm.controls['dateOfBirth'].value)
      && !this.hasText(this.applicationDataForm.controls['phone'].value)
      && !this.hasText(this.applicationDataForm.controls['email'].value)) {
      errors.push({ field: 'lookup', label: 'Date of Birth, Phone Number, or Email', step: 'Admin' });
    }

    if (this.applicationDataForm.controls['phone'].hasError('pattern')) {
      errors.push({ field: 'phone', label: 'Phone Number', step: 'Admin' });
    }

    if (this.applicationDataForm.controls['email'].hasError('email')) {
      errors.push({ field: 'email', label: 'Email', step: 'Admin' });
    }

    if (!this.personApplicationData.person?.id) {
      errors.push({ field: 'linkedPerson', label: 'Matching Individual', step: 'Admin' });
    }

    if (this.applicationAttestationForm.controls['attestation'].value !== true) {
      errors.push({ field: 'attestation', label: 'Attestation', step: 'Admin' });
    }

    if (!esignature) {
      errors.push({ field: 'esignature', label: 'Electronic Signature', step: 'Admin' });
    }

    return errors;
  }

  approveExistingPersonApplication(){
    const dateData = new Date(this.issueDate);    
    dateData.setDate(dateData.getDate());
    const issueDate = dateData.toISOString().substring(0, 10)    
    dateData.setMonth(dateData.getMonth() + 30)
    const recertDate = dateData.toISOString().substring(0, 10)    
    dateData.setMonth(dateData.getMonth() + 6)
    const expirationDate = dateData.toISOString().substring(0, 10)   

    let person: IPerson = this.personApplicationData.person as IPerson;
    let personAddress: any = null;
    let personContactInformation: any = null;
    let personRepContactInformation: IPersonContactInformation = {
      id: 0,
      person: person,
      contactInformation: null as any,
    };
    let personCertificate: IPersonCertificate = {
      id: 0, 
      person: person, 
      stage: this.personApplicationData.renewal ? CERTIFICATE_STAGE_TYPES.recert : CERTIFICATE_STAGE_TYPES.initial, 
      certificateNumber: this.certificateNumber ?? '',
      codeType: this.personApplicationData.certificateType, 
      controlNumber: this.applicationDataForm.controls.previousApplication.value, 
      issueDate: issueDate,
      recertificationDate: recertDate, 
      expirationDate: expirationDate,
      dateOfLetter: null,
      application: this.buildPersonApplicationSavePayload(),
    };
    let address: any = null;
    let contact: any = null;
    let repContact: IContactInformation = {
      label: this.applicationDataForm.controls.repLabel.value, 
      phone: this.applicationDataForm.controls.repPhone.value, 
      cell: this.applicationDataForm.controls.repAlternatePhone.value, 
      fax: this.applicationDataForm.controls.repFaxPhone.value, 
      email: this.applicationDataForm.controls.repEmail.value, 
      website: this.applicationDataForm.controls.repWebsite.value, 
      id: 0, 
      contactType: "representative", 
      ext: ""
    };

    if(repContact.cell !== "" || repContact.email !== "" || repContact.fax !== "" || repContact.phone !== "" || repContact.website !== ""){
      this.commonService.saveContactInformation(repContact).subscribe(result=>{
        personRepContactInformation.contactInformation = result;
        this.personService.savePersonContactInfo(personContactInformation).subscribe(result=>{          
        },error=>{this.toastr.error("An error occurred saving the representative person contact information: ",error)})
      },error=>{this.toastr.error("An error occurred saving the representative contact information: ",error)})
    }    

    this.personService.getPersonAddressesByPersonId(this.personApplicationData.person!.id).subscribe(result=>{
      personAddress = result[0];
      address = personAddress.address;

      address.street = this.personApplicationData.mailingStreet;
      address.city = this.personApplicationData.mailingCity;
      address.state = this.personApplicationData.mailingState;
      address.zip = this.personApplicationData.mailingZip;
      address.county = this.personApplicationData.mailingCounty;
      personAddress.isCurrentMailing = true;
      
      this.commonService.saveAddress(address).subscribe(result=>{},error=>{this.toastr.error("An error occurred saving the address: ", error)});
    },error=>{this.toastr.error("An error occurred getting the person addresses: ", error)})

    this.personService.getPersonContactInformationByPersonId(this.personApplicationData.person!.id).subscribe(result=>{
      personContactInformation = result;
      contact = personContactInformation.contactInformation;

      contact.phone = this.personApplicationData.phone;
      contact.cell = this.personApplicationData.alternatePhone;
      contact.fax = this.personApplicationData.faxPhone;
      contact.email = this.personApplicationData.email;
      contact.website = this.personApplicationData.website;  

      this.commonService.saveContactInformation(contact).subscribe(result=>{},error=>{this.toastr.error("An error occurred saving the contact information: ", error)});
    },error=>{this.toastr.error("An error occurred getting the person contact information: ", error)})

    this.personService.savePersonCertificate(personCertificate).subscribe(result=>{},error=>{this.toastr.error("An error occurred saving the person certificate: ", error)});

    person.title = this.personApplicationData.title;
    person.firstName = this.personApplicationData.firstName;
    person.middleName = this.personApplicationData.middleName;
    person.lastName = this.personApplicationData.lastName;
    person.ssn = this.personApplicationData.ssn;
    person.dateOfBirth = this.toApprovalIsoString(this.personApplicationData.dateOfBirth);
    person.response = this.personApplicationData.isPublic ? "Yes" : "No";      
        
    this.personService.savePerson(person).subscribe(result=>{},error=>{this.toastr.error("An error occurred saving the person: ", error)});    
    const applicationToSave = this.buildPersonApplicationSavePayload();
    this.personApplicationData = applicationToSave;
    this.personService.SavePersonApplication(applicationToSave).subscribe(result=>{
      this.toastr.success("The application was successfully approved")        
      this.exitDialog();
    },error=>{this.toastr.error("An error occurred saving the person application: ",error)})
  }

  approveNewPersonApplication(){
    const dateData = new Date(this.issueDate);    
    dateData.setDate(dateData.getDate() + 1);
    const issueDate = dateData.toISOString().substring(0, 10)    
    dateData.setMonth(dateData.getMonth() + 30)
    const recertDate = dateData.toISOString().substring(0, 10)    
    dateData.setMonth(dateData.getMonth() + 6)
    const expirationDate = dateData.toISOString().substring(0, 10)           
    
    let personRepContactInformation: IPersonContactInformation = {
      id: 0,
      person: null as any,
      contactInformation: null as any,
    };
    let repContact: IContactInformation = {
      label: this.applicationDataForm.controls.repLabel.value, 
      phone: this.applicationDataForm.controls.repPhone.value, 
      cell: this.applicationDataForm.controls.repAlternatePhone.value, 
      fax: this.applicationDataForm.controls.repFaxPhone.value, 
      email: this.applicationDataForm.controls.repEmail.value, 
      website: this.applicationDataForm.controls.repWebsite.value, 
      id: 0, 
      contactType: "representative", 
      ext: ""
    };
    
    const person: IPerson = {
      id: 0,
      title: this.personApplicationData.title,
      firstName: this.personApplicationData.firstName,
      middleName: this.personApplicationData.middleName,
      lastName: this.personApplicationData.lastName,
      ssn: this.personApplicationData.ssn,
      dateOfBirth: this.toApprovalIsoString(this.personApplicationData.dateOfBirth),
      response: this.personApplicationData.isPublic ? "Yes" : "No",
      inactive: false,
      certificates: []
    }
    const personContactInformation: IPersonContactInformation = {
      id: 0,
      person: null as any,
      contactInformation: null as any,
    }
    const address: IAddress = {
      id: 0,
      contactType: "Work",
      street: this.personApplicationData.mailingStreet,
      city: this.personApplicationData.mailingCity,
      state: this.personApplicationData.mailingState,
      zip: this.personApplicationData.mailingZip,
      county: this.personApplicationData.mailingCounty,
    }    
    const personAddress: IPersonAddress = {
      id: 0,
      person: null as any,
      address: null as any,
      isCurrentMailing: true,
      isCurrentPhysical: false,
    }    
    const accountAffiliation: IAccountAffiliation = {
      id: 0,
      userID: this.personApplicationData.userID,
      firm: null as any,
      atp: null,
      status: 'Active',
      requestedStatus: 'Active',
      lastUpdatedBy: '',
      userName: '',
      person: null,
    };
    const contactInformation: IContactInformation = {
      id: 0,
      contactType: "Work",
      phone: this.personApplicationData.phone,
      cell: this.personApplicationData.alternatePhone,
      fax: this.personApplicationData.faxPhone,
      ext: "",
      email: this.personApplicationData.email,
      website: this.personApplicationData.website,
      label: "",
    };
    const certificateData: IPersonCertificate = {
      id: 0, 
      person: null as any, 
      stage: CERTIFICATE_STAGE_TYPES.initial, 
      codeType: this.personApplicationData.certificateType, 
      certificateNumber: this.certificateNumber ?? '',
      controlNumber: this.applicationDataForm.controls.previousApplication.value, 
      issueDate: issueDate,
      recertificationDate: recertDate, 
      expirationDate: expirationDate,
      dateOfLetter: null,
      application: this.buildPersonApplicationSavePayload(),
    };

    this.personService.savePerson(person).subscribe(result=>{
      this.toastr.success("The person was successfully created")
      personContactInformation.person = result;
      personAddress.person = result;
      certificateData.person = result;
      personRepContactInformation.person = result;      
      this.personService.savePersonCertificate(certificateData).subscribe(result=>{
        certificateData.id = result.id;        
      },error=>{
        this.toastr.error("There was an error adding the certificate: ", error);
      })

      this.commonService.saveAddress(address).subscribe(add => {
        personAddress.address = add;
        this.personService.savePersonAddress(personAddress).subscribe(pAdd => {
          personAddress.id = pAdd.id;
        },error=>{this.toastr.error("An error occurred saving the person address: ", error)});
      },error=>{this.toastr.error("An error occurred saving the address: ", error)});

      
      this.commonService.saveContactInformation(contactInformation).subscribe(result=>{
        personContactInformation.contactInformation = result;
        this.personService.savePersonContactInfo(personContactInformation).subscribe(result=>{          
        },error=>{this.toastr.error("An error occurred saving the person contact information: ",error)})
      },error=>{this.toastr.error("An error occurred saving the contact information: ",error)}) 
      
      if(repContact.cell !== "" || repContact.email !== "" || repContact.fax !== "" || repContact.phone !== "" || repContact.website !== ""){
        this.commonService.saveContactInformation(repContact).subscribe(result=>{          
          repContact = result;        
          personRepContactInformation.contactInformation = result;
          this.personService.savePersonContactInfo(personRepContactInformation).subscribe(result=>{              
          },error=>{this.toastr.error("An error occurred saving the representative person contact information: ",error)})
        },error=>{this.toastr.error("An error occurred saving the representative contact information: ",error)})
      }            
      
      this.personApplicationData.person = result;
      accountAffiliation.person = result;
      
      this.commonService.saveAccountAffiliation(accountAffiliation).subscribe(result=>{},error=>{this.toastr.error("An error occurred saving the account affiliation: ",error)})
      const applicationToSave = this.buildPersonApplicationSavePayload();
      this.personApplicationData = applicationToSave;
      this.personService.SavePersonApplication(applicationToSave).subscribe(result=>{        
        this.exitDialog();
      },error=>{this.toastr.error("An error occurred saving the person application: ",error)})
    },error=>{this.toastr.error("An error occurred saving the application person: ", error)})    
  }

  updateApplicationDeficiencyDebounceString(event: any){    
    this.applicationDeficiencyDebounceString.next(event.target.value);    
  }
  filterDeficiencyTable(){    
    this.filteredApplicationDeficiencies = this.applicationDeficiencies.filter(x=>
      (x.id + ' ' + x.description).toLowerCase().includes(this.applicationDeficiencyFilterString)
      && ((this.applicationDeficiencyStatusFilterString.value === "both") ? true : (x.isResolved.toString() === this.applicationDeficiencyStatusFilterString.value)) 
    );            
    this.updateDeficiencySource();  
  }

  updateDeficiencySource(){
    this.deficiencyDataSource = new MatTableDataSource<any>(this.filteredApplicationDeficiencies);
    this.deficiencyDataSource.sort = this.deficiencySort;
    this.deficiencyDataSource.paginator = this.deficiencyPaginator;
  }

  editDeficiency(deficiencyToEdit: any){
    let dialogRef = this.dialog.open(AddIndividualApplicationDeficiencyComponent, {
      width: this.isMobile ? '90%' : '40%',
      data: {deficiency: deficiencyToEdit, personApplication: this.personApplicationData},});
    dialogRef.afterClosed().subscribe(result => {
      if (result){
        this.updatePersonApplicationDeficiencies();
      }        
    });
  }
  
  deleteDeficiency(deficiency: IPersonApplicationDeficiency) {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {message:'Are you sure you want to remove the application deficiency with ID: <strong>' + deficiency.id + '</strong>?',title:'Delete Application Deficiency'},});
    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.personService.deletePersonApplicationDeficiency(deficiency.id).subscribe(result=>{
          this.updatePersonApplicationDeficiencies();
        },error=>{})
      }
    });
  }
  toggleDeficiencyResolved(deficiencyToEdit: IPersonApplicationDeficiency, isChecked: boolean){
    deficiencyToEdit.isResolved = isChecked;
    this.personService.savePersonApplicationDeficiency(deficiencyToEdit).subscribe(result=>{
      this.filterDeficiencyTable();
    },error=>{console.error("There was an error saving the deficiency:", error)});
  }
  updatePersonApplicationDeficiencies(){
    this.personService.getPersonApplicationDeficiencies(this.personApplicationData.id).subscribe(result=>{      
      this.applicationDeficiencies = result.sort((a, b) => {
        // Check if both items have the same IsResolved status
        if (a.isResolved === b.isResolved) {
          // If so, sort by id in descending order
          return b.id - a.id;
        } else {
          // If not, ensure items with IsResolved as false come first
          return a.isResolved ? 1 : -1;
        }
      });
      this.filterDeficiencyTable();      
    },error=>{});
  }
  exitDialog(){
    this.dialogRef.close(this.updateOnClose);
  }

  displayLimit(text: any, limit: string){
    let newText = text;
    if (typeof text === 'string' && text.length > parseInt(limit)){
      newText = newText.substring(0, limit) + "...";
    }    
    return newText;
  }
}
